
import { useEffect, useState } from "react";
import useTasks from "../../../hooks/useTasks";
import useDisplayNotifications from "../../../hooks/useDisplayNotifications";
import { ITask } from "../../../data_models/task";
import { IFreelancer } from "../../../data_models/freelancer";
import Lightbox from "../../common/Lightbox";
import LoadingIndicator from "../../common/LoadingIndicator";
import { DisplayNotificationType } from "../../../models/DisplayNotification";
import useProjects from "../../../hooks/useProjects";
import { IProject } from "../../../data_models/project";
import { Chip } from "../../common/Chip";
import TextInputField from "../../common/TextInputField";
import { FilledButton } from "../../common/Button";
import useInvitations from "../../../hooks/useInvitations";
import { ECurrency, Money } from "../../../data_models/money";
import { getFreelancerName } from "../../../utils/freelancerUtils";


interface SelectTaskForInviteLightboxProps {
    onClose: () => void;
    freelancer: IFreelancer;
}

const SelectTaskForInviteLightbox: React.FC<SelectTaskForInviteLightboxProps> = ({ onClose, freelancer }) => {

    const [loading, setLoading] = useState(false);
    const [loadingInvite, setLoadingInvite] = useState(false);
    const [tasks, setTasks] = useState<ITask[]>([]);
    const [projects, setProjects] = useState<IProject[]>([]);
    const [selectedTask, setSelectedTask] = useState<ITask>();
    const [offerAmount, setOfferAmount] = useState<number>(0);

    // pagination
    const [tasksPage, setTasksPage] = useState(1);
    const [tasksLimit, setTasksLimit] = useState(10);
    const [projectsPage, setProjectsPage] = useState(1);
    const [projectsLimit, setProjectsLimit] = useState(10);


    const { fetchUnassignedTasksForPm } = useTasks();
    const { fetchMyProjectsAsPm } = useProjects();
    const { newInvitation } = useInvitations();
    const { addNotification } = useDisplayNotifications();

    useEffect(() => {
        setLoading(true);
        let fetchProjectsDone = false;
        let fetchTasksDone = false;

        fetchMyProjectsAsPm(projectsPage, projectsLimit).then(projects => {
            setProjects(projects);
        }).catch(() => {
            addNotification({
                message: 'Error fetching projects as project manager',
                type: DisplayNotificationType.ERROR,
            });
        }).finally(() => {
            fetchProjectsDone = true;
            if (fetchProjectsDone && fetchTasksDone) {
                setLoading(false);
            }
        });

        fetchUnassignedTasksForPm(tasksPage, tasksLimit).then(tasks => {
            setTasks(tasks);
        }).catch(() => {
            addNotification({
                message: 'Error fetching tasks',
                type: DisplayNotificationType.ERROR,
            });
        }).finally(() => {
            fetchTasksDone = true;
            if (fetchProjectsDone && fetchTasksDone) {
                setLoading(false);
            }
        });
    }, [])

    useEffect(() => {
        setOfferAmount(selectedTask?.estimatedCredits || 0);
    }, [selectedTask])

    const onInvite = async () => {
        if (!selectedTask) return;
        if (selectedTask.estimatedCredits && offerAmount > selectedTask.estimatedCredits) {
            addNotification({
                message: `The offer can at most be ${selectedTask.estimatedCredits} credits`,
                type: DisplayNotificationType.WARNING,
            })
        } else {
            setLoadingInvite(true);
            try {
                const payout: number = offerAmount;
                if (freelancer._id &&
                    selectedTask?._id &&
                    getProjectByTaskId(selectedTask._id!)!._id &&
                    payout
                ) {
                    const invite = await newInvitation(
                        freelancer._id!,
                        selectedTask._id!,
                        getProjectByTaskId(selectedTask._id!)!._id!,
                        payout);
                    addNotification({
                        message: "Invitation successfully sent",
                        type: DisplayNotificationType.SUCCESS,
                    })
                } else {
                    throw new Error("An unknown error occured");
                }
                onClose();

            } catch (error: any) {
                addNotification({
                    message: error.message,
                    type: DisplayNotificationType.ERROR,
                })
            } finally {
                setLoadingInvite(false);
            }
        }

    }

    const getProjectByTaskId = (taskId: string) => {
        const project = projects.find(project => project.tasks?.some(task => task === taskId));
        return project;
    }




    return (
        <Lightbox
            content={
                <div className="flex flex-col items-center space-y-4" >
                    <h1>Select a task</h1>
                    {loading
                        ? <div className=""><LoadingIndicator /></div>
                        : tasks.length === 0
                            ? <div>No tasks found</div>
                            : <div className="flex flex-col space-y-2">
                                {tasks.map((task, index) => {
                                    return (
                                        <div key={index}
                                            className={`cursor-pointer w-full rounded-xl p-2 border-2 border-transparent hover:border-primary dark:hover:border-dark-primary
                                            ${selectedTask?._id === task._id ? 'bg-background2 dark:bg-dark-background2' : ''}`}
                                            onClick={() => { setSelectedTask(task); }}>
                                            <div className="flex flex-row justify-between w-full">
                                                {/* title, projectTitle, skills, tools, estimatedpayout */}
                                                <div className="flex flex-col">
                                                    <h3>{getProjectByTaskId(task._id!)?.title}</h3>
                                                    <h4>{task.title}</h4>
                                                </div>

                                                {task.estimatedCredits &&
                                                    <div className="flex flex-col items-end">
                                                        <label>Payout Credits</label>
                                                        <h5>
                                                            {task.estimatedCredits} credits
                                                        </h5>
                                                    </div>}

                                            </div>
                                            <div className="flex flex-wrap space-x-4 mt-2">
                                                {task.requiredSkills && task.requiredSkills.length > 0 &&
                                                    <div className="flex flex-col items-start">
                                                        <label>Skills</label>
                                                        <div className="flex flex-row">
                                                            {task.requiredSkills.map((skill, index) => {
                                                                return (
                                                                    <Chip>
                                                                        {skill}
                                                                    </Chip>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>}
                                                {task.requiredTools && task.requiredTools.length > 0 &&
                                                    <div className="flex flex-col items-start">
                                                        <label>Tools</label>
                                                        <div className="flex flex-row">
                                                            {task.requiredTools.map((tool, index) => {
                                                                return (
                                                                    <Chip theme="secondary">
                                                                        {tool}
                                                                    </Chip>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>}
                                            </div>
                                        </div>
                                    );
                                })}

                                <div>
                                    {!selectedTask
                                        ? <p>Click on a task to select it.</p>
                                        : <div className="flex flex-col items-center space-y-4">
                                            <p>Selected: <span className="font-bold">{selectedTask.title}</span>. You can offer at most <span className="font-bold">{selectedTask.estimatedCredits} credits</span> to <span className="font-bold">{getFreelancerName(freelancer)}</span> to complete this task.</p>
                                            <div className="max-w-40">
                                                <TextInputField
                                                    value={offerAmount.toString()}
                                                    label="Your Offer (USD)"
                                                    onChange={(e) => setOfferAmount(parseFloat(e.target.value))}
                                                    type="number"
                                                />
                                            </div>
                                            <FilledButton
                                                fullWidth
                                                isLoading={loadingInvite}
                                                onClick={onInvite}
                                            >Invite</FilledButton>
                                        </div>}


                                </div>
                            </div>}

                </div>}
            onClose={onClose}
        />

    );
}

export default SelectTaskForInviteLightbox;